import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/Button'
import { Checkbox, Field } from '@/components/ui/Field'
import { FormSheet } from '@/components/ui/FormSheet'
import { useFormDraft } from '@/components/ui/useFormDraft'
import { Input } from '@/components/ui/Input'
import type { CargoPosition } from '@/types/domain'

/* The cargo position editor — where UJ-1 is won or lost.
 *
 * The PRD's counter-signal is the 50 CBM declaration that arrives as a 90 CBM
 * truck. So volume is REQUIRED here, and chemical cargo adds MSDS and the
 * transport certificate to the checklist (FR-12, FR-13). The sheet does not
 * merely collect these — it says what an omission blocks, at the moment of
 * entry, so the refusal to send later is never a surprise.
 */

export interface CargoDraft {
  description: string
  hsCode: string
  shipperName: string
  packages: string
  packageType: string
  weightKg: string
  volumeCbm: string
  chargeableWeightKg: string
  lengthCm: string
  widthCm: string
  heightCm: string
  isChemical: boolean
  hasMsds: boolean
  hasTransportCertificate: boolean
}

/** Blank is allowed where the field is optional; a non-numeric entry is not. */
function isBlankOrNumber(raw: string): boolean {
  const normalised = raw.replace(/\s/g, '').replace(',', '.')
  return normalised === '' || /^\d+(\.\d+)?$/.test(normalised)
}

function isPositiveNumber(raw: string): boolean {
  const normalised = raw.replace(/\s/g, '').replace(',', '.')
  return /^\d+(\.\d+)?$/.test(normalised) && Number(normalised) > 0
}

export function CargoFormSheet({
  open,
  onClose,
  record,
  onSubmit,
  onDelete,
  pending,
  error,
}: {
  open: boolean
  onClose: () => void
  record?: CargoPosition
  onSubmit: (values: CargoDraft) => void
  /** Removes an existing position. Absent when adding — there is nothing to
   *  remove yet, and a delete affordance on a blank form is a trap. */
  onDelete?: () => void
  pending?: boolean
  /** A refusal from the server, shown above the fields with the draft intact. */
  error?: string
}) {
  const { t } = useTranslation()

  /* A half-pressed delete must not survive the sheet closing: reopening on
     another position with the confirmation already showing would put the
     dangerous button under a click meant for something else.

     Keyed on the record rather than reset in an effect — an effect that
     setStates on `open` costs a second render pass every time the sheet opens,
     and React's own guidance is to derive from a key instead. Changing the key
     remounts the confirmation in its closed state for free. */
  const [confirmingDelete, setConfirmingDelete] = useState(false)
  const [confirmKey, setConfirmKey] = useState<string | null>(null)
  const currentKey = open ? (record?.id ?? 'new') : null
  if (confirmKey !== currentKey) {
    setConfirmKey(currentKey)
    setConfirmingDelete(false)
  }

  const [draft, setDraft, touched, markTouched] = useFormDraft<CargoDraft>(
    open ? (record?.id ?? 'new') : null,
    () =>
      record
        ? {
            description: record.description,
            hsCode: record.hsCode ?? '',
            shipperName: record.shipperName ?? '',
            packages: String(record.packages),
            packageType: record.packageType,
            weightKg: record.weightKg ?? '',
            volumeCbm: record.volumeCbm ?? '',
            chargeableWeightKg: record.chargeableWeightKg ?? '',
            lengthCm: record.lengthCm ?? '',
            widthCm: record.widthCm ?? '',
            heightCm: record.heightCm ?? '',
            isChemical: record.isChemical,
            hasMsds: record.hasMsds ?? false,
            hasTransportCertificate: record.hasTransportCertificate ?? false,
          }
        : emptyCargo,
  )

  const descriptionError =
    touched && !draft.description.trim() ? t('common.form.required') : undefined
  const packagesError =
    touched && !isPositiveNumber(draft.packages)
      ? t('common.form.invalidNumber')
      : undefined
  /* Volume is needed before the offer goes out, and NOT before the position is
     saved. The distinction is the whole design of FR-12: a form that refused
     the save would be answered by inventing a number, and the invented number
     is what reaches the client as the 50 CBM declaration behind a 90 CBM
     truck. So an absent volume is a WARNING here — it names what it will
     block — while the checklist reports it and the send refuses it.

     A non-numeric entry is still refused: that is a typo, not an omission. */
  const volumeMissing = draft.volumeCbm.trim() === ''
  const volumeError =
    touched && !volumeMissing && !isPositiveNumber(draft.volumeCbm)
      ? t('common.form.invalidNumber')
      : undefined
  const weightError =
    touched && !isBlankOrNumber(draft.weightKg)
      ? t('common.form.invalidNumber')
      : undefined
  /* Optional like the gross weight, and refused only when it is a typo: most
     road cargo has no separate chargeable weight at all. */
  const chargeableWeightError =
    touched && !isBlankOrNumber(draft.chargeableWeightKg)
      ? t('common.form.invalidNumber')
      : undefined

  const invalid =
    !draft.description.trim() ||
    !isPositiveNumber(draft.packages) ||
    (!volumeMissing && !isPositiveNumber(draft.volumeCbm)) ||
    !isBlankOrNumber(draft.weightKg) ||
    !isBlankOrNumber(draft.chargeableWeightKg)

  return (
    <FormSheet
      open={open}
      onClose={onClose}
      title={record ? t('quotes.cargo.edit') : t('quotes.cargo.add')}
      pending={pending}
      // The server's refusal outranks the local one: it is about this save,
      // where the field errors are about the form in general.
      error={error ?? (touched && invalid ? t('common.form.fixFields') : undefined)}
      onSubmit={() => {
        markTouched()
        if (invalid) return
        onSubmit(draft)
      }}
    >
      <Field label={t('quotes.cargo.position')} required error={descriptionError}>
        {(props) => (
          <Input
            value={draft.description}
            onChange={(e) => setDraft({ ...draft, description: e.target.value })}
            {...props}
          />
        )}
      </Field>

      <Field label={t('quotes.cargo.hsCode')} hint={t('common.form.optional')}>
        {(props) => (
          <Input
            value={draft.hsCode}
            onChange={(e) => setDraft({ ...draft, hsCode: e.target.value })}
            className="font-mono"
            {...props}
          />
        )}
      </Field>

      <div className="flex gap-2.5">
        <Field
          label={t('quotes.cargo.packages')}
          required
          error={packagesError}
          className="flex-1"
        >
          {(props) => (
            <Input
              inputMode="numeric"
              value={draft.packages}
              onChange={(e) => setDraft({ ...draft, packages: e.target.value })}
              className="asa-num"
              {...props}
            />
          )}
        </Field>

        <Field label={t('quotes.cargo.packageType')} className="flex-1">
          {(props) => (
            <Input
              value={draft.packageType}
              onChange={(e) => setDraft({ ...draft, packageType: e.target.value })}
              {...props}
            />
          )}
        </Field>
      </div>

      {/* Who ships THIS position (FR-12). Blank on the ordinary quote, where
          the header names one shipper; it earns its place on the consolidated
          truck, where the offer has to say which boxes came from whom. */}
      <Field label={t('quotes.cargo.shipper')} hint={t('quotes.cargo.shipperHint')}>
        {(props) => (
          <Input
            value={draft.shipperName}
            onChange={(e) => setDraft({ ...draft, shipperName: e.target.value })}
            {...props}
          />
        )}
      </Field>

      <div className="flex gap-2.5">
        <Field label={t('quotes.cargo.weight')} error={weightError} className="flex-1">
          {(props) => (
            <Input
              inputMode="decimal"
              value={draft.weightKg}
              onChange={(e) => setDraft({ ...draft, weightKg: e.target.value })}
              className="asa-num"
              {...props}
            />
          )}
        </Field>

        <Field
          label={t('quotes.cargo.volume')}
          error={volumeError}
          // Says what an omission will block, at the moment of entry, so the
          // send refusing later is never a surprise (FR-12).
          hint={
            volumeError
              ? undefined
              : volumeMissing
                ? t('quotes.cargo.volumeRequired')
                : t('quotes.cargo.volumeHint')
          }
          className="flex-1"
        >
          {(props) => (
            <Input
              inputMode="decimal"
              value={draft.volumeCbm}
              onChange={(e) => setDraft({ ...draft, volumeCbm: e.target.value })}
              className="asa-num"
              {...props}
            />
          )}
        </Field>
      </div>

      {/* What the carrier bills when that is not the gross weight (FR-13).
          Entered rather than computed: the volumetric divisor belongs to the
          carrier, and asserting one of our own would invent the figure. */}
      <Field
        label={t('quotes.cargo.chargeableWeight')}
        error={chargeableWeightError}
        hint={chargeableWeightError ? undefined : t('quotes.cargo.chargeableWeightHint')}
      >
        {(props) => (
          <Input
            inputMode="decimal"
            value={draft.chargeableWeightKg}
            onChange={(e) =>
              setDraft({ ...draft, chargeableWeightKg: e.target.value })
            }
            className="asa-num"
            {...props}
          />
        )}
      </Field>

      <fieldset className="flex flex-col gap-1.5">
        <legend className="text-text-body pb-1.5 text-[12.5px] leading-none">
          {t('quotes.cargo.dimensions')}
        </legend>
        <div className="flex gap-2.5">
          {(['lengthCm', 'widthCm', 'heightCm'] as const).map((key) => (
            <Field key={key} label={t(`quotes.cargo.${key}`)} className="flex-1">
              {(props) => (
                <Input
                  inputMode="decimal"
                  value={draft[key]}
                  onChange={(e) => setDraft({ ...draft, [key]: e.target.value })}
                  className="asa-num"
                  {...props}
                />
              )}
            </Field>
          ))}
        </div>
      </fieldset>

      {/* Chemical cargo adds MSDS and the transport certificate to the
          checklist (FR-13). Ticking the box is what reveals them, so the two
          extra requirements appear at the moment they start applying. */}
      <Checkbox
        label={t('quotes.cargo.isChemical')}
        checked={draft.isChemical}
        onCheckedChange={(checked) => setDraft({ ...draft, isChemical: checked })}
      />

      {draft.isChemical ? (
        <div className="border-attention/35 bg-attention-soft/40 flex flex-col gap-2 rounded-md border px-3 py-2.5">
          <p className="text-text-body text-[12px] leading-snug font-light">
            {t('quotes.cargo.chemicalNote')}
          </p>
          <Checkbox
            label={t('quotes.cargo.hasMsds')}
            checked={draft.hasMsds}
            onCheckedChange={(checked) => setDraft({ ...draft, hasMsds: checked })}
          />
          <Checkbox
            label={t('quotes.cargo.hasTransportCertificate')}
            checked={draft.hasTransportCertificate}
            onCheckedChange={(checked) =>
              setDraft({ ...draft, hasTransportCertificate: checked })
            }
          />
        </div>
      ) : null}

      {/* Removing sits at the FOOT of the form, apart from the footer's Save,
          rather than beside it: FormSheet keeps the dangerous action off the
          same muscle memory as the safe one, and a delete button next to Save
          is exactly that collision. Two presses, because the first is easy to
          hit by accident and a removed position cannot be recovered. */}
      {onDelete ? (
        <div className="border-line-hair mt-1 border-t pt-3">
          {confirmingDelete ? (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-text-body flex-1 text-[12.5px] leading-snug">
                {t('quotes.cargo.deleteConfirm')}
              </span>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setConfirmingDelete(false)}
                disabled={pending}
              >
                {t('common.actions.cancel')}
              </Button>
              <Button
                variant="danger"
                size="sm"
                onClick={onDelete}
                disabled={pending}
              >
                {t('quotes.cargo.deleteConfirmAction')}
              </Button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmingDelete(true)}
              disabled={pending}
              className="text-negative cursor-pointer text-[12.5px] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {t('quotes.cargo.delete')}
            </button>
          )}
        </div>
      ) : null}
    </FormSheet>
  )
}

const emptyCargo: CargoDraft = {
  description: '',
  hsCode: '',
  shipperName: '',
  packages: '',
  packageType: '',
  weightKg: '',
  volumeCbm: '',
  chargeableWeightKg: '',
  lengthCm: '',
  widthCm: '',
  heightCm: '',
  isChemical: false,
  hasMsds: false,
  hasTransportCertificate: false,
}
